/**
 * WallSectionSidebar Component
 *
 * Sidebar for the map editor that lists all wall section regions.
 * Wall sections and regions are unified - each region IS a wall section.
 *
 * Features:
 * - Lists all existing regions
 * - Add new region (enter name, then draw polygon)
 * - Edit existing region (drag vertices)
 * - Delete region (removes wall section entirely)
 *
 * @example
 * ```tsx
 * <WallSectionSidebar
 *   regions={floorPlan.regions}
 *   selectedWallSection={selectedWallSection}
 *   editingRegionName={editingRegionName}
 *   isDrawing={isDrawing}
 *   onSelectForDrawing={startDrawing}
 *   onSelectForEditing={selectRegionForEditing}
 *   onDeleteRegion={deleteRegion}
 *   onCancel={handleCancel}
 * />
 * ```
 */

import { useState, type CSSProperties, type FormEvent } from 'react';
import type { WallSectionRegion } from '@flash/shared';

/**
 * Props for WallSectionSidebar component
 */
export interface WallSectionSidebarProps {
  /** All regions defined on the floor plan */
  regions: WallSectionRegion[];
  /** Currently selected wall section for drawing (null if not drawing) */
  selectedWallSection: string | null;
  /** Currently editing region name (null if not editing) */
  editingRegionName: string | null;
  /** Whether currently in drawing mode */
  isDrawing: boolean;
  /** Callback to start drawing a new region with the given name */
  onSelectForDrawing: (wallSectionName: string) => void;
  /** Callback to select a region for editing */
  onSelectForEditing: (wallSectionName: string) => void;
  /** Callback to delete a region */
  onDeleteRegion: (wallSectionName: string) => Promise<void>;
  /** Callback to rename a region */
  onRenameRegion: (oldName: string, newName: string) => Promise<void>;
  /** Callback to cancel the current operation (drawing/editing) */
  onCancel: () => void;
  /** Whether a save is in progress */
  isSaving?: boolean;
  /** Whether sidebar is collapsed (mobile only) */
  isCollapsed?: boolean;
  /** Callback to toggle sidebar collapse */
  onToggleCollapse?: () => void;
  /** Whether in mobile view */
  isMobile?: boolean;
}

/**
 * WallSectionSidebar Component
 *
 * Sidebar for managing wall section regions in the editor.
 */
export const WallSectionSidebar = ({
  regions,
  selectedWallSection,
  editingRegionName,
  isDrawing,
  onSelectForDrawing,
  onSelectForEditing,
  onDeleteRegion,
  onRenameRegion,
  onCancel,
  isSaving = false,
  isCollapsed = false,
  onToggleCollapse,
  isMobile = false,
}: WallSectionSidebarProps) => {
  const [newRegionName, setNewRegionName] = useState('');
  const [deletingRegion, setDeletingRegion] = useState<string | null>(null);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [renamingRegion, setRenamingRegion] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);

  // Get sorted region names
  const regionNames = regions
    .map((r) => r.wallSectionName)
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));

  /**
   * Handle input change - clear error when user types
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewRegionName(e.target.value);
    if (duplicateError) {
      setDuplicateError(null);
    }
  };

  /**
   * Handle starting to draw a new region
   */
  const handleAddRegion = (e: FormEvent) => {
    e.preventDefault();
    const trimmedName = newRegionName.trim();
    if (!trimmedName) return;

    // Check for duplicates (case-insensitive)
    if (regionNames.some((name) => name.toLowerCase() === trimmedName.toLowerCase())) {
      setDuplicateError(`A region named "${trimmedName}" already exists`);
      return;
    }

    setDuplicateError(null);
    onSelectForDrawing(trimmedName);
    setNewRegionName('');
  };

  /**
   * Handle confirming region deletion
   */
  const handleConfirmDelete = async () => {
    if (!deletingRegion) return;

    try {
      await onDeleteRegion(deletingRegion);
    } finally {
      setDeletingRegion(null);
    }
  };

  /**
   * Handle starting to rename a region
   */
  const handleStartRename = (regionName: string) => {
    setRenamingRegion(regionName);
    setRenameValue(regionName);
    setRenameError(null);
  };

  /**
   * Handle rename input change
   */
  const handleRenameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRenameValue(e.target.value);
    if (renameError) {
      setRenameError(null);
    }
  };

  /**
   * Handle confirming region rename
   */
  const handleConfirmRename = async () => {
    if (!renamingRegion) return;

    const trimmedName = renameValue.trim();
    if (!trimmedName) {
      setRenameError('Name cannot be empty');
      return;
    }

    // Check for duplicates (case-insensitive, excluding current name)
    if (
      trimmedName.toLowerCase() !== renamingRegion.toLowerCase() &&
      regionNames.some((name) => name.toLowerCase() === trimmedName.toLowerCase())
    ) {
      setRenameError(`A region named "${trimmedName}" already exists`);
      return;
    }

    try {
      await onRenameRegion(renamingRegion, trimmedName);
      setRenamingRegion(null);
      setRenameValue('');
      setRenameError(null);
    } catch (err) {
      setRenameError(err instanceof Error ? err.message : 'Failed to rename');
    }
  };

  /**
   * Handle canceling rename
   */
  const handleCancelRename = () => {
    setRenamingRegion(null);
    setRenameValue('');
    setRenameError(null);
  };

  const isSelected = (regionName: string) =>
    (isDrawing && selectedWallSection === regionName) || editingRegionName === regionName;

  // On mobile when collapsed, show only a toggle button
  if (isMobile && isCollapsed) {
    return (
      <aside style={collapsedSidebarStyle}>
        <button
          type="button"
          onClick={onToggleCollapse}
          style={toggleButtonStyle}
          aria-label="Open wall sections panel"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span style={toggleBadgeStyle}>{regions.length}</span>
        </button>
      </aside>
    );
  }

  return (
    <aside style={{ ...sidebarStyle, ...(isMobile ? mobileSidebarStyle : {}) }}>
      {/* Mobile close button */}
      {isMobile && onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          style={mobileCloseButtonStyle}
          aria-label="Close panel"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}

      {/* Header */}
      <header style={headerStyle}>
        <h2 style={titleStyle}>Wall Sections</h2>
        <p style={subtitleStyle}>
          {regions.length} region{regions.length !== 1 ? 's' : ''} defined
        </p>
      </header>

      {/* Add New Region Form */}
      <form onSubmit={handleAddRegion} style={addFormStyle}>
        <div style={inputWrapperStyle}>
          <input
            type="text"
            value={newRegionName}
            onChange={handleInputChange}
            placeholder="New section name..."
            style={{
              ...addInputStyle,
              ...(duplicateError ? addInputErrorStyle : {}),
            }}
            maxLength={50}
            disabled={isSaving || isDrawing}
            aria-invalid={!!duplicateError}
            aria-describedby={duplicateError ? 'region-name-error' : undefined}
          />
          {duplicateError && (
            <p id="region-name-error" style={errorTextStyle}>
              {duplicateError}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={!newRegionName.trim() || isSaving || isDrawing}
          style={{
            ...addButtonStyle,
            ...(!newRegionName.trim() || isSaving || isDrawing ? addButtonDisabledStyle : {}),
          }}
        >
          Draw
        </button>
      </form>

      {/* Region List */}
      <ul style={listStyle}>
        {regionNames.length === 0 ? (
          <li style={emptyStateStyle}>
            <p style={emptyTextStyle}>No regions defined yet.</p>
            <p style={emptyHintStyle}>
              Enter a name above and click Draw to create your first region.
            </p>
          </li>
        ) : (
          regionNames.map((regionName) => {
            const isItemSelected = isSelected(regionName);

            return (
              <li key={regionName} style={listItemStyle}>
                <div
                  style={{
                    ...itemContainerStyle,
                    ...(isItemSelected ? itemContainerSelectedStyle : {}),
                  }}
                >
                  <button
                    type="button"
                    onClick={() => onSelectForEditing(regionName)}
                    disabled={isSaving || isDrawing || !!editingRegionName}
                    style={{
                      ...itemButtonStyle,
                      ...(isSaving || isDrawing || !!editingRegionName
                        ? itemButtonDisabledStyle
                        : {}),
                    }}
                    aria-pressed={isItemSelected}
                  >
                    {/* Status indicator - always green since all items are regions */}
                    <span style={statusDotStyle} title="Region defined" />

                    {/* Region name */}
                    <span style={itemNameStyle}>{regionName}</span>
                  </button>

                  {/* Rename region button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!isSaving && !isDrawing && !editingRegionName) {
                        handleStartRename(regionName);
                      }
                    }}
                    disabled={isSaving || isDrawing || !!editingRegionName}
                    style={{
                      ...actionButtonStyle,
                      ...(isSaving || isDrawing || !!editingRegionName
                        ? actionButtonDisabledStyle
                        : {}),
                    }}
                    title="Rename region"
                    aria-label={`Rename ${regionName}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>

                  {/* Delete region button */}
                  <button
                    type="button"
                    onClick={() => {
                      if (!isSaving && !isDrawing && !editingRegionName) {
                        setDeletingRegion(regionName);
                      }
                    }}
                    disabled={isSaving || isDrawing || !!editingRegionName}
                    style={{
                      ...actionButtonStyle,
                      ...(isSaving || isDrawing || !!editingRegionName
                        ? actionButtonDisabledStyle
                        : {}),
                    }}
                    title="Delete region"
                    aria-label={`Delete ${regionName}`}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </li>
            );
          })
        )}
      </ul>

      {/* Cancel Button when drawing/editing */}
      {(isDrawing || editingRegionName) && (
        <div style={footerStyle}>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            style={{
              ...cancelButtonStyle,
              ...(isSaving ? itemButtonDisabledStyle : {}),
            }}
          >
            Cancel {isDrawing ? 'Drawing' : 'Editing'}
          </button>
        </div>
      )}

      {/* Help Text */}
      <div style={helpTextContainerStyle}>
        <p style={helpTextStyle}>
          {isDrawing ? (
            <>
              Click on the map to add points.
              <br />
              Double-click or press Done to finish.
            </>
          ) : editingRegionName ? (
            <>
              Drag vertices to adjust.
              <br />
              Click Done when finished.
            </>
          ) : (
            <>Click a region to edit, or enter a name to draw a new one.</>
          )}
        </p>
      </div>

      {/* Delete Region Confirmation Dialog */}
      {deletingRegion && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Delete Region</h3>
            <p style={dialogTextStyle}>
              Are you sure you want to delete <strong>{deletingRegion}</strong>?
            </p>
            <p style={warningTextStyle}>
              Routes assigned to this wall section will need to be reassigned.
            </p>
            <div style={dialogActionsStyle}>
              <button
                type="button"
                onClick={() => setDeletingRegion(null)}
                style={dialogCancelButtonStyle}
              >
                Cancel
              </button>
              <button type="button" onClick={handleConfirmDelete} style={dialogDeleteButtonStyle}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Region Dialog */}
      {renamingRegion && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={dialogTitleStyle}>Rename Region</h3>
            <p style={dialogTextStyle}>
              Enter a new name for <strong>{renamingRegion}</strong>:
            </p>
            <div style={renameInputWrapperStyle}>
              <input
                type="text"
                value={renameValue}
                onChange={handleRenameInputChange}
                style={{
                  ...renameInputStyle,
                  ...(renameError ? renameInputErrorStyle : {}),
                }}
                maxLength={50}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmRename();
                  } else if (e.key === 'Escape') {
                    handleCancelRename();
                  }
                }}
              />
              {renameError && <p style={renameErrorTextStyle}>{renameError}</p>}
            </div>
            <div style={dialogActionsStyle}>
              <button type="button" onClick={handleCancelRename} style={dialogCancelButtonStyle}>
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                disabled={!renameValue.trim()}
                style={{
                  ...dialogConfirmButtonStyle,
                  ...(!renameValue.trim() ? dialogConfirmButtonDisabledStyle : {}),
                }}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

// Styles
const sidebarStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  width: '280px',
  minWidth: '280px',
  height: '100%',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  borderRight: '1px solid var(--color-border, #e5e7eb)',
  overflow: 'hidden',
};

const mobileSidebarStyle: CSSProperties = {
  position: 'absolute',
  top: '56px', // Below toolbar
  left: 0,
  bottom: 0,
  zIndex: 100,
  width: '85%',
  maxWidth: '320px',
  boxShadow: '4px 0 12px rgba(0, 0, 0, 0.15)',
};

const collapsedSidebarStyle: CSSProperties = {
  position: 'absolute',
  top: '72px', // 56px toolbar + 16px margin
  left: '16px',
  zIndex: 100,
  // Add padding and background to create a backdrop that hides canvas edges
  padding: '4px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  borderRadius: '16px',
};

const toggleButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 16px',
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  border: 'none',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
};

const toggleBadgeStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '24px',
  height: '24px',
  padding: '0 8px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  borderRadius: '12px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'white',
};

const mobileCloseButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '12px',
  right: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  padding: 0,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '8px',
  color: 'var(--color-text-secondary, #6b7280)',
  cursor: 'pointer',
};

const headerStyle: CSSProperties = {
  padding: '16px',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
};

const subtitleStyle: CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const addFormStyle: CSSProperties = {
  display: 'flex',
  gap: '8px',
  padding: '12px 16px',
  borderBottom: '1px solid var(--color-border, #e5e7eb)',
  alignItems: 'center',
};

const inputWrapperStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const addInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  color: 'var(--color-text-primary, #1f2937)',
  height: '38px', // Match button height
  boxSizing: 'border-box',
};

const addInputErrorStyle: CSSProperties = {
  borderColor: 'var(--color-error, #ef4444)',
};

const errorTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: 'var(--color-error, #ef4444)',
};

const addButtonStyle: CSSProperties = {
  padding: '10px 16px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minWidth: '56px',
  height: '38px',
};

const addButtonDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted, #9ca3af)',
  cursor: 'not-allowed',
};

const listStyle: CSSProperties = {
  flex: 1,
  margin: 0,
  padding: '8px',
  listStyle: 'none',
  overflowY: 'auto',
};

const listItemStyle: CSSProperties = {
  marginBottom: '4px',
};

const emptyStateStyle: CSSProperties = {
  padding: '24px 16px',
  textAlign: 'center',
};

const emptyTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
};

const emptyHintStyle: CSSProperties = {
  margin: '8px 0 0',
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
};

const itemContainerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '4px',
  backgroundColor: 'transparent',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'transparent',
  borderRadius: '8px',
  transition: 'all 0.15s ease',
};

const itemContainerSelectedStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary-light, #eff6ff)',
  borderColor: 'var(--color-primary, #3b82f6)',
};

const itemButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flex: 1,
  minWidth: 0,
  padding: '8px',
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

const itemButtonDisabledStyle: CSSProperties = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

const statusDotStyle: CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  flexShrink: 0,
  backgroundColor: '#10b981',
};

const itemNameStyle: CSSProperties = {
  flex: 1,
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
  textAlign: 'left',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const actionButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  marginLeft: '4px',
  padding: 0,
  backgroundColor: 'transparent',
  border: 'none',
  borderRadius: '4px',
  color: 'var(--color-text-secondary, #6b7280)',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
};

const actionButtonDisabledStyle: CSSProperties = {
  opacity: 0.4,
  cursor: 'not-allowed',
};

const footerStyle: CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};

const cancelButtonStyle: CSSProperties = {
  width: '100%',
  padding: '10px 16px',
  backgroundColor: 'var(--color-bg-secondary, #f3f4f6)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  color: 'var(--color-text-primary, #1f2937)',
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
};

const helpTextContainerStyle: CSSProperties = {
  padding: '12px 16px',
  borderTop: '1px solid var(--color-border, #e5e7eb)',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
};

const helpTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.75rem',
  color: 'var(--color-text-secondary, #6b7280)',
  lineHeight: 1.5,
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1001,
  padding: '20px',
};

const dialogStyle: CSSProperties = {
  backgroundColor: 'var(--color-bg-primary, #ffffff)',
  borderRadius: '12px',
  padding: '24px',
  maxWidth: '360px',
  width: '100%',
};

const dialogTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: '1.125rem',
  fontWeight: 600,
  color: 'var(--color-text-primary, #1f2937)',
  marginBottom: '12px',
};

const dialogTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary, #6b7280)',
  marginBottom: '12px',
};

const warningTextStyle: CSSProperties = {
  margin: 0,
  fontSize: '0.875rem',
  color: 'var(--color-warning, #d97706)',
  backgroundColor: 'var(--color-badge-warning-bg, #fef3c7)',
  padding: '12px',
  borderRadius: '6px',
  marginBottom: '16px',
};

const dialogActionsStyle: CSSProperties = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-end',
};

const dialogCancelButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-bg-tertiary, #f3f4f6)',
  color: 'var(--color-text-primary, #1f2937)',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const dialogDeleteButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-error, #ef4444)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const dialogConfirmButtonStyle: CSSProperties = {
  padding: '10px 20px',
  backgroundColor: 'var(--color-primary, #3b82f6)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  fontSize: '0.875rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const dialogConfirmButtonDisabledStyle: CSSProperties = {
  backgroundColor: 'var(--color-text-muted, #9ca3af)',
  cursor: 'not-allowed',
};

const renameInputWrapperStyle: CSSProperties = {
  marginBottom: '16px',
};

const renameInputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '0.875rem',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: '6px',
  backgroundColor: 'var(--color-bg-secondary, #f9fafb)',
  color: 'var(--color-text-primary, #1f2937)',
  boxSizing: 'border-box',
};

const renameInputErrorStyle: CSSProperties = {
  borderColor: 'var(--color-error, #ef4444)',
};

const renameErrorTextStyle: CSSProperties = {
  margin: '4px 0 0',
  fontSize: '0.75rem',
  color: 'var(--color-error, #ef4444)',
};

export default WallSectionSidebar;
